/**
 * How long a 429 asks the caller to wait before asking once more (2026-11-03).
 *
 * One module, so every place the web app waits out a 429 waits the same way: fetchApi's read retry and the
 * dashboard's token refresh (lib/api/client.ts), and the learner's token refresh (lib/api/portal-auth.ts).
 */

/**
 * Where a 429 says how long to wait, in seconds. @nestjs/throttler sets
 * `Retry-After-<bucket>` (it suffixes every bucket but `default`); other
 * limiters set a plain `Retry-After`. Cross-origin the browser shows only the
 * ones apps/api/src/main.ts lists in `exposedHeaders`.
 */
const RETRY_AFTER_HEADERS = ['Retry-After', 'Retry-After-short', 'Retry-After-medium', 'Retry-After-long'] as const;
const RETRY_AFTER_DEFAULT_MS = 1_000;
/** A browser tab waits up to 5 s; a server render (no window) at most 1 s, as someone is waiting for the page it builds. */
const RETRY_AFTER_BROWSER_CAP_MS = 5_000;
const RETRY_AFTER_SERVER_CAP_MS = 1_000;

/** The longest wait the 429 names, capped (5 s in a browser, 1 s on the server); 1 s when it names none that is a number. */
export function retryAfterMs(headers: Pick<Headers, 'get'>): number {
  const cap = typeof window === 'undefined' ? RETRY_AFTER_SERVER_CAP_MS : RETRY_AFTER_BROWSER_CAP_MS;
  let seconds: number | null = null;
  for (const name of RETRY_AFTER_HEADERS) {
    const raw = headers.get(name)?.trim();
    if (!raw) continue;
    const value = Number(raw);
    if (Number.isFinite(value) && value >= 0) seconds = Math.max(seconds ?? 0, value);
  }
  return Math.min(seconds === null ? RETRY_AFTER_DEFAULT_MS : seconds * 1_000, cap);
}
